'use client';

import { motion, type Transition } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { BaseLineWorld } from '@/app/components/icons/BaseLineWorld';
import { ClientLink } from '@/app/components/shared/ClientLink';
import VideoLazyLoad from '@/app/components/shared/VideoLazyLoad';
import { reportEvent } from '@/app/utils';
import { useDeferredMediaRender } from '../useDeferredMediaRender';
import { videoItemsConfig } from './constants';

// 动画变体配置
const fadeInUpVariants = {
  hidden: { opacity: 0, y: 150 },
  visible: (custom: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: custom * 0.2,
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1] as const,
    },
  }),
};

const AiTemplate = () => {
  const t = useTranslations('home3.aiTemplate');
  const { ref, shouldRenderMedia } = useDeferredMediaRender<HTMLDivElement>();

  // 渲染视频项
  const renderVideoItems = () => {
    if (!shouldRenderMedia) {
      return null;
    }

    return videoItemsConfig.map((config) => (
      <motion.div
        key={config.id}
        className={config.className}
        initial="hidden"
        whileInView="visible"
        viewport={config.viewport}
        variants={fadeInUpVariants}
        custom={config.custom}
        {...(config.transition ? { transition: config.transition as Transition } : {})}
      >
        <VideoLazyLoad
          poster={config.poster}
          videoSrc={config.videoSrc}
          className="aspect-video h-full w-full"
          preload="none"
          alt={`AI template ${config.id}`}
        />
      </motion.div>
    ));
  };

  return (
    <div ref={ref} className="relative h-auto w-full overflow-hidden">
      <div className="flex flex-col items-center justify-start overflow-hidden pt-[160px] md:pt-24">
        <div className="mb-6 flex h-[36px] w-[64px] items-center justify-center rounded-full bg-system-white24 md:hidden">
          <BaseLineWorld className="size-6 text-system-white" />
        </div>
        <h2 className="mb-3 text-center font-medium text-[48px] md:mb-0 md:text-[36px] md:leading-none">
          {t('title')}
        </h2>
        <div className="my-4 hidden h-[2px] w-[50px] bg-system-white24 md:block" />
        <div className="mb-8 font-normal text-base text-system-white opacity-65 md:w-[259px] md:text-center md:text-[14px]">
          {t('description')}
        </div>
        <div className="flex items-center justify-center md:absolute md:right-[calc(50%-77px)] md:bottom-[69px]">
          <ClientLink
            href="/templates"
            className="z-10 flex cursor-pointer items-center justify-center rounded-full bg-system-blue03 px-8 py-3 font-medium text-[14px] text-system-white leading-[30px]"
            onClick={() => {
              reportEvent('element_click', {
                element_name: 'Template-Create Now',
              });
            }}
          >
            {t('exploreMore')}
          </ClientLink>
        </div>
      </div>
      <div className="relative h-[700px] sm:h-[455px] md:h-[550px]">{renderVideoItems()}</div>
    </div>
  );
};

export default AiTemplate;
